import React, { useEffect, useMemo, useState } from "react";
import { auth } from "../../firebase";
import { useAuth } from "../../AuthContext";

// Admin-only "Bars to call": bars WITHOUT an owner that customers are looking
// at, hottest first, so you know who to call about claiming their bar.
// Sort by clicking a column name. Your status and notes save automatically.

const URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/outreachBars";
const LOADER_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/importAreaBars";
const UPDATES_URL = "https://us-central1-bar-buzz-a6645.cloudfunctions.net/createUpdatesCheckout";

async function post(body, url = URL) {
  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
  return data;
}

const ALL_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME",
  "MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI",
  "SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY",
];

const COLUMNS = [
  { key: "heat", label: "Heat", num: true, tip: "Views + 3×page opens + 5×taps + 8×saves this week + 2×favorites" },
  { key: "trend", label: "Trend", num: true, tip: "Views this week vs the week before" },
  { key: "name", label: "Bar" },
  { key: "city", label: "City" },
  { key: "openMinutes", label: "Opens today", num: true, tip: "From Google, fetched when you click Get opening times" },
  { key: "websiteState", label: "Website", tip: "None, dead, Facebook only, or has a website" },
  { key: "scanLabel", label: "Website scan", tip: "What happened when Read websites checked this bar" },
  { key: "found", label: "Specials / events in app", num: true },
  { key: "views7", label: "Views 7d", num: true },
  { key: "opens7", label: "Opened 7d", num: true },
  { key: "taps7", label: "Call/Map/Web taps 7d", num: true },
  { key: "favorites", label: "Favorited", num: true },
  { key: "views30", label: "Views 30d", num: true },
  { key: "lastActivity", label: "Last seen" },
  { key: "status", label: "Status" },
];

const cell = { padding: "10px 8px", borderBottom: "1px solid #241E33", verticalAlign: "top", fontSize: "15px" };
const input = {
  minHeight: "40px", padding: "0 10px", borderRadius: "8px", border: "1px solid #3A3350",
  background: "#0B0B12", color: "#fff", font: "500 15px Inter, sans-serif",
};
const btn = {
  minHeight: "40px", padding: "0 14px", borderRadius: "8px", border: "none",
  font: "700 15px Inter, sans-serif", cursor: "pointer",
};

function trendLabel(r) {
  if (r.newThisWeek) return { text: "New", color: "#4ADE80" };
  if (r.trend == null) return { text: "–", color: "#8A84A0" };
  if (r.trend > 0) return { text: `▲ ${r.trend}%`, color: "#4ADE80" };
  if (r.trend < 0) return { text: `▼ ${Math.abs(r.trend)}%`, color: "#F87171" };
  return { text: "0%", color: "#B8B0C8" };
}

const WEBSITE_LABEL = {
  none: { text: "None", color: "#4ADE80" },
  dead: { text: "Dead link", color: "#4ADE80" },
  facebook: { text: "Facebook only", color: "#FACC15" },
  has: { text: "Has website", color: "#B8B0C8" },
};
const NO_REAL_SITE = new Set(["none", "dead", "facebook"]);

// Plain-English version of what the website reader recorded.
function scanLabel(r) {
  const s = r.scan || "";
  if (!s) return "Not scanned yet";
  if (s === "ok") return r.found > 0 ? "Read: found specials/events" : "Read: nothing found";
  const map = {
    "no website": "No website",
    "website is dead (button hidden)": "Dead website",
    "website didn't load": "Website didn't load",
    "website has no readable text": "Website has no readable text",
    "website asks not to be scanned": "Website blocks scanning",
    "Facebook/Instagram only": "Facebook/Instagram only",
    "chain website (skipped)": "Chain website (skipped)",
    "AI couldn't read it": "AI couldn't read it",
    "claimed (left alone)": "Has owner (skipped)",
    "read by the old loader": "Read earlier (Load Bars tab)",
    "never scanned": "Not scanned (added by hand or from the gaming list)",
    error: "Error while scanning",
  };
  return map[s] || s;
}

function toCsv(rows) {
  const head = ["Heat", "Trend %", "Bar", "Address", "City", "State", "Phone", "Website", "Opens today", "Website status", "Website scan", "Specials", "Events", "Views 7d", "Views prior 7d",
    "Opened 7d", "Calls 7d", "Directions 7d", "Website taps 7d", "Saves 7d", "Favorited", "Views 30d",
    "Last seen", "Charity linked", "Status", "Notes"];
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows.map((r) => [r.heat, r.newThisWeek ? "new" : r.trend ?? "", r.name, r.address, r.city, r.state, r.phone,
    r.website, r.openLabel || "", WEBSITE_LABEL[r.websiteState]?.text || "", r.scanLabel, r.specials, r.events, r.views7, r.viewsPrev7, r.opens7, r.calls7, r.directions7, r.websiteTaps7, r.saves7, r.favorites,
    r.views30, r.lastActivity, r.charityLinked ? "yes" : "", r.status, r.note].map(esc).join(","));
  return [head.join(","), ...lines].join("\n");
}

export default function AdminOutreach() {
  const { role } = useAuth();
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [generatedAt, setGeneratedAt] = useState("");

  const [sortKey, setSortKey] = useState("heat");
  const [sortDir, setSortDir] = useState("desc");
  const [stateFilter, setStateFilter] = useState("MN");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [phoneOnly, setPhoneOnly] = useState(false);
  const [noSiteOnly, setNoSiteOnly] = useState(false);
  const [scanFilter, setScanFilter] = useState("ALL");
  const [showClaimed, setShowClaimed] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [hoursBusy, setHoursBusy] = useState(false);
  const [hoursById, setHoursById] = useState({}); // barId -> { openLabel, openMinutes, week }
  const [scanningId, setScanningId] = useState("");
  const [scanResult, setScanResult] = useState({}); // barId -> message
  const [payingOnly, setPayingOnly] = useState(false);
  const [linkFor, setLinkFor] = useState({}); // barId -> checkout link or message

  const load = async (st = stateFilter) => {
    setLoading(true);
    setError("");
    try {
      const data = await post({ action: "list", state: st === "ALL" ? undefined : st });
      setRows(
        data.rows.map((r) => {
          const row = { ...r, taps7: r.calls7 + r.directions7 + r.websiteTaps7, found: (r.specials || 0) + (r.events || 0) };
          return { ...row, scanLabel: scanLabel(row) };
        })
      );
      setStatuses(data.statuses || []);
      setGeneratedAt(data.generatedAt);
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const scanOptions = useMemo(() => [...new Set(rows.map((r) => r.scanLabel))].sort(), [rows]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((r) => {
      if (!showClaimed && r.claimed && !r.managedUpdates) return false;
      if (payingOnly && !r.managedUpdates) return false;
      if (phoneOnly && !r.phone) return false;
      if (noSiteOnly && !NO_REAL_SITE.has(r.websiteState)) return false;
      if (scanFilter !== "ALL" && r.scanLabel !== scanFilter) return false;
      if (statusFilter === "NONE" && r.status) return false;
      if (statusFilter !== "ALL" && statusFilter !== "NONE" && r.status !== statusFilter) return false;
      if (q && !`${r.name} ${r.city} ${r.address}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const col = COLUMNS.find((c) => c.key === sortKey);
    const dir = sortDir === "asc" ? 1 : -1;
    return list.sort((a, b) => {
      let va = a[sortKey];
      let vb = b[sortKey];
      if (sortKey === "trend") {
        va = a.newThisWeek ? 100000 : a.trend ?? -100000;
        vb = b.newThisWeek ? 100000 : b.trend ?? -100000;
      }
      if (sortKey === "openMinutes") {
        // Unknown / closed today always go to the bottom.
        const ha = hoursById[a.id]?.openMinutes;
        const hb = hoursById[b.id]?.openMinutes;
        if (ha == null && hb == null) return 0;
        if (ha == null) return 1;
        if (hb == null) return -1;
        return (ha - hb) * dir;
      }
      if (col?.num) return ((va || 0) - (vb || 0)) * dir;
      return String(va || "").localeCompare(String(vb || "")) * dir;
    });
  }, [rows, sortKey, sortDir, search, statusFilter, phoneOnly, noSiteOnly, scanFilter, showClaimed, payingOnly, hoursById]);

  if (role !== "admin") return <p style={{ color: "#fff" }}>Admins only.</p>;

  const clickSort = (key) => {
    if (key === sortKey) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else {
      setSortKey(key);
      const col = COLUMNS.find((c) => c.key === key);
      setSortDir(key === "openMinutes" ? "asc" : col?.num ? "desc" : "asc");
    }
  };

  // Opening times for the top 50 bars currently shown (about 4¢ each the
  // first time, about 2¢ after). Earliest opener first when done.
  const getHours = async () => {
    const ids = shown.filter((r) => !hoursById[r.id]).slice(0, 50).map((r) => r.id);
    if (ids.length === 0) {
      setSortKey("openMinutes");
      setSortDir("asc");
      return;
    }
    setHoursBusy(true);
    setError("");
    try {
      const out = await post({ action: "hours", barIds: ids });
      setHoursById((prev) => {
        const next = { ...prev };
        (out.hours || []).forEach((h) => (next[h.barId] = h));
        return next;
      });
      setSortKey("openMinutes");
      setSortDir("asc");
    } catch (e) {
      setError(`Couldn't get opening times: ${e.message}`);
    }
    setHoursBusy(false);
  };

  const saveNote = async (r, patch) => {
    const next = { ...r, ...patch };
    setRows((prev) => prev.map((x) => (x.id === r.id ? next : x)));
    setSavingId(r.id);
    try {
      const out = await post({ action: "note", barId: r.id, status: next.status, note: next.note });
      setRows((prev) => prev.map((x) => (x.id === r.id ? { ...x, contactedAt: out.contactedAt } : x)));
    } catch (e) {
      setError(`Couldn't save the note for ${r.name}: ${e.message}`);
    }
    setSavingId("");
  };

  // Read one bar's website right now (about 5¢), even if it has an owner.
  const scanNow = async (r) => {
    if (r.claimed && !window.confirm(`${r.name} has an owner. Add what's on their website to their page anyway?`)) return;
    setScanningId(r.id);
    setScanResult((m) => ({ ...m, [r.id]: "Reading website…" }));
    try {
      const out = await post({ action: "load", placeIds: [r.id], force: true }, LOADER_URL);
      const x = (out.results || [])[0] || {};
      let msg = scanLabel({ scan: x.status, found: (x.specials || 0) + (x.events || 0) });
      if (x.status === "ok") msg = `Added ${x.specials || 0} specials and ${x.events || 0} events`;
      if (out.stop) msg = out.stop;
      setScanResult((m) => ({ ...m, [r.id]: msg }));
    } catch (e) {
      setScanResult((m) => ({ ...m, [r.id]: `Couldn't scan: ${e.message}` }));
    }
    setScanningId("");
  };

  // A $10/month "We Post It For You" signup link for this bar, to text or
  // email to them. No account needed on their side.
  const makeLink = async (r) => {
    setLinkFor((m) => ({ ...m, [r.id]: "Making link…" }));
    try {
      const out = await post({ barId: r.id }, UPDATES_URL);
      setLinkFor((m) => ({ ...m, [r.id]: out.url }));
    } catch (e) {
      setLinkFor((m) => ({ ...m, [r.id]: `Couldn't make a link: ${e.message}` }));
    }
  };

  const download = () => {
    const withHours = shown.map((r) => ({ ...r, openLabel: hoursById[r.id]?.openLabel || "" }));
    const blob = new Blob([toCsv(withHours)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = window.URL.createObjectURL(blob);
    a.download = `bars-to-call-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => window.URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <div style={{ color: "#fff" }}>
      <h2 style={{ marginBottom: "6px" }}>Bars to call</h2>
      <p style={{ color: "#B8B0C8", marginBottom: "16px", lineHeight: 1.5, maxWidth: "900px" }}>
        Bars <strong>without an owner</strong>. Pick a state to see <strong>every</strong> bar there with no real
        website (none, dead, or Facebook only), plus any bar customers looked at; hottest first. Click a column name to
        sort. Pick a status and type notes as you call; they save automatically and stay here next time.
      </p>

      {error && (
        <div style={{ background: "#3A1420", color: "#FFC2D1", padding: "12px 14px", borderRadius: "10px", marginBottom: "14px" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
        <input style={{ ...input, minWidth: "220px" }} placeholder="Search bar or city" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select
          style={input}
          value={stateFilter}
          onChange={(e) => {
            setStateFilter(e.target.value);
            load(e.target.value);
          }}
          disabled={loading}
          title="One state: every unclaimed bar there with no real website, plus bars customers looked at. All states: only bars customers looked at."
        >
          <option value="ALL">All states (looked-at bars only)</option>
          {ALL_STATES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select style={input} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">Any status</option>
          <option value="NONE">Not contacted yet</option>
          {statuses.filter(Boolean).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select style={input} value={scanFilter} onChange={(e) => setScanFilter(e.target.value)}>
          <option value="ALL">Any website scan result</option>
          {scanOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <label style={{ display: "flex", gap: "6px", alignItems: "center", color: "#D6D2E4" }}>
          <input type="checkbox" checked={phoneOnly} onChange={(e) => setPhoneOnly(e.target.checked)} /> Has a phone number
        </label>
        <label style={{ display: "flex", gap: "6px", alignItems: "center", color: "#D6D2E4" }}>
          <input type="checkbox" checked={noSiteOnly} onChange={(e) => setNoSiteOnly(e.target.checked)} /> No real website
        </label>
        <label style={{ display: "flex", gap: "6px", alignItems: "center", color: "#D6D2E4" }}>
          <input type="checkbox" checked={payingOnly} onChange={(e) => setPayingOnly(e.target.checked)} /> Paying $10 (we update)
        </label>
        <label style={{ display: "flex", gap: "6px", alignItems: "center", color: "#D6D2E4" }}>
          <input type="checkbox" checked={showClaimed} onChange={(e) => setShowClaimed(e.target.checked)} /> Include claimed bars
        </label>
        <button style={{ ...btn, background: "#2A2238", color: "#fff" }} onClick={() => load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
        <button
          style={{ ...btn, background: "#8B5CF6", color: "#fff" }}
          onClick={getHours}
          disabled={hoursBusy || !shown.length}
          title="Looks up today's opening time on Google for the first 50 bars shown"
        >
          {hoursBusy ? "Getting times…" : "Get opening times"}
        </button>
        <button style={{ ...btn, background: "#FF3D81", color: "#0B0B12" }} onClick={download} disabled={!shown.length}>
          Download spreadsheet
        </button>
      </div>

      <p style={{ color: "#8A84A0", fontSize: "14px", marginBottom: "8px" }}>
        {loading ? "Loading…" : `${shown.length.toLocaleString()} bars`}
        {generatedAt && !loading && ` · numbers as of ${new Date(generatedAt).toLocaleString()}`}
        {Object.keys(hoursById).length > 0 && " · Opening times: Google (hover a time to see the whole week)"}
        {" · "}“Views” = times the bar showed up for a customer; “Opened” = they tapped into its page.
      </p>

      <div style={{ overflowX: "auto", background: "#17131F", borderRadius: "12px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "1100px" }}>
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  title={c.tip || ""}
                  onClick={() => clickSort(c.key)}
                  style={{
                    ...cell, textAlign: c.num ? "right" : "left", cursor: "pointer", userSelect: "none",
                    color: sortKey === c.key ? "#FF6FA3" : "#B8B0C8", fontSize: "13px", whiteSpace: "nowrap",
                  }}
                >
                  {c.label}
                  {sortKey === c.key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                </th>
              ))}
              <th style={{ ...cell, color: "#B8B0C8", fontSize: "13px", textAlign: "left" }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const t = trendLabel(r);
              return (
                <tr key={r.id}>
                  <td style={{ ...cell, textAlign: "right", fontWeight: 800, color: "#FF6FA3" }}>{r.heat}</td>
                  <td style={{ ...cell, textAlign: "right", color: t.color, whiteSpace: "nowrap" }}>{t.text}</td>
                  <td style={{ ...cell, minWidth: "220px" }}>
                    <div style={{ fontWeight: 700 }}>
                      {r.name}
                      {r.claimed && <span style={{ color: "#B9A6FF", fontWeight: 500 }}> · has owner</span>}
                      {r.charityLinked && <span style={{ color: "#B9A6FF", fontWeight: 500 }}> · charity linked</span>}
                    </div>
                    {r.managedUpdates && (
                      <div style={{ margin: "4px 0", display: "inline-block", padding: "2px 8px", borderRadius: "6px", background: "#4ADE80", color: "#0B0B12", fontSize: "12px", fontWeight: 800 }}>
                        PAYING $10 · WE UPDATE{r.managedEmail ? ` · ${r.managedEmail}` : ""}
                        {r.permissionBy
                          ? ` · Permission: ${r.permissionBy}, ${new Date(r.permissionAt).toLocaleDateString()}`
                          : " · Permission: not on file"}
                      </div>
                    )}
                    {r.phone && (
                      <a href={`tel:${r.phone.replace(/[^0-9+]/g, "")}`} style={{ color: "#FF6FA3", fontSize: "14px" }}>
                        {r.phone}
                      </a>
                    )}
                    {!r.managedUpdates && (
                      <div style={{ marginTop: "6px" }}>
                        {linkFor[r.id] && linkFor[r.id].startsWith("http") ? (
                          <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                            <input readOnly value={linkFor[r.id]} onFocus={(e) => e.target.select()} style={{ ...input, minHeight: "32px", fontSize: "12px", width: "180px" }} />
                            <button
                              style={{ ...btn, minHeight: "32px", padding: "0 10px", fontSize: "13px", background: "#8B5CF6", color: "#fff" }}
                              onClick={() => navigator.clipboard?.writeText(linkFor[r.id])}
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          <button
                            style={{ ...btn, minHeight: "32px", padding: "0 10px", fontSize: "13px", background: "#2A2238", color: "#B9A6FF" }}
                            onClick={() => makeLink(r)}
                            title="Makes a $10/month signup link to text or email to this bar"
                          >
                            {linkFor[r.id] === "Making link…" ? "Making link…" : "Get $10 signup link"}
                          </button>
                        )}
                        {linkFor[r.id] && !linkFor[r.id].startsWith("http") && linkFor[r.id] !== "Making link…" && (
                          <div style={{ fontSize: "12px", color: "#FFC2D1", marginTop: "4px" }}>{linkFor[r.id]}</div>
                        )}
                      </div>
                    )}
                    {r.website && (
                      <a href={r.website} target="_blank" rel="noreferrer" style={{ color: "#B8B0C8", fontSize: "13px", marginLeft: r.phone ? "10px" : 0 }}>
                        website
                      </a>
                    )}
                  </td>
                  <td style={cell}>{[r.city, r.state].filter(Boolean).join(", ")}</td>
                  <td style={{ ...cell, textAlign: "right", whiteSpace: "nowrap" }} title={(hoursById[r.id]?.week || []).join("\n")}>
                    {hoursById[r.id]?.openLabel || "–"}
                  </td>
                  <td style={{ ...cell, whiteSpace: "nowrap", color: WEBSITE_LABEL[r.websiteState]?.color, fontWeight: 600 }}>
                    {WEBSITE_LABEL[r.websiteState]?.text || "–"}
                  </td>
                  <td style={{ ...cell, color: r.scan === "ok" && r.found > 0 ? "#4ADE80" : "#D6D2E4", minWidth: "170px" }}>
                    {r.scanLabel}
                    {r.website && (
                      <div style={{ marginTop: "6px" }}>
                        <button
                          style={{ ...btn, minHeight: "32px", padding: "0 10px", fontSize: "13px", background: "#2A2238", color: "#fff" }}
                          disabled={scanningId === r.id}
                          onClick={() => scanNow(r)}
                        >
                          {scanningId === r.id ? "Scanning…" : "Scan website now"}
                        </button>
                      </div>
                    )}
                    {scanResult[r.id] && (
                      <div style={{ marginTop: "4px", fontSize: "13px", color: "#FFC2D1" }}>{scanResult[r.id]}</div>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    {r.found > 0 ? `${r.specials} / ${r.events}` : "–"}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>{r.views7}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{r.opens7}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{r.taps7}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{r.favorites}</td>
                  <td style={{ ...cell, textAlign: "right" }}>{r.views30}</td>
                  <td style={{ ...cell, whiteSpace: "nowrap" }}>{r.lastActivity || "–"}</td>
                  <td style={cell}>
                    <select
                      style={{ ...input, minHeight: "36px", maxWidth: "170px" }}
                      value={r.status}
                      onChange={(e) => saveNote(r, { status: e.target.value })}
                    >
                      {statuses.map((s) => (
                        <option key={s || "none"} value={s}>{s || "Not contacted"}</option>
                      ))}
                    </select>
                    {r.contactedAt && (
                      <div style={{ color: "#8A84A0", fontSize: "12px", marginTop: "4px" }}>
                        {savingId === r.id ? "Saving…" : `updated ${new Date(r.contactedAt).toLocaleDateString()}`}
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, minWidth: "220px" }}>
                    <textarea
                      defaultValue={r.note}
                      placeholder="Who you talked to, when to call back…"
                      onBlur={(e) => e.target.value !== r.note && saveNote(r, { note: e.target.value })}
                      style={{ ...input, width: "100%", minHeight: "60px", padding: "8px 10px", resize: "vertical" }}
                    />
                  </td>
                </tr>
              );
            })}
            {!loading && shown.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1} style={{ ...cell, color: "#B8B0C8", textAlign: "center", padding: "30px" }}>
                  No bars match. Customers' views show up here as they use the app.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
